import { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  DataState,
  PageHeader,
  TabSwitcher,
  Table,
  useToast,
  type Column,
} from "@vms/ui-web";
import { useApi, useCanIDo } from "../auth/AuthProvider";
import { useOrgContext } from "../context/OrgContext";
import { useQuery } from "../api/queries";

type BookingStatus = "pending" | "confirmed" | "synced" | "active" | "completed" | "cancelled";

interface BookingRow {
  id: string;
  organisationId: string;
  venueId: string;
  status: BookingStatus;
  customerName: string;
  customerEmail?: string | null;
  customerPhone?: string | null;
  scheduledFor: string;
  durationMin: number;
  partnerSource?: string | null;
  externalRef?: string | null;
  eventId?: string | null;
  createdAt: string;
}

const TABS: { key: BookingStatus | "all"; label: string }[] = [
  { key: "pending", label: "Pending" },
  { key: "confirmed", label: "Confirmed" },
  { key: "synced", label: "Synced" },
  { key: "active", label: "Active" },
  { key: "completed", label: "Completed" },
  { key: "cancelled", label: "Cancelled" },
  { key: "all", label: "All" },
];

const STATUS_COLOURS: Record<BookingStatus, string> = {
  pending: "bg-amber-100 text-amber-700",
  confirmed: "bg-blue-100 text-blue-700",
  synced: "bg-indigo-100 text-indigo-700",
  active: "bg-green-100 text-green-700",
  completed: "bg-gray-100 text-gray-700",
  cancelled: "bg-red-100 text-red-700",
};

interface BookingsProps {
  /** When set, filter further to a specific venue id (used by the venue
   *  bookings page once CU.6 lands). */
  venueId?: string;
}

export function Bookings({ venueId }: BookingsProps = {}) {
  const { currentOrg, venues } = useOrgContext();
  const api = useApi();
  const canIDo = useCanIDo();
  const { addToast } = useToast();

  const [tab, setTab] = useState<BookingStatus | "all">("pending");
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState<BookingRow | null>(null);

  const endpoint = currentOrg ? `/api/bookings?organisationId=${currentOrg.id}` : null;
  const bookings = useQuery<BookingRow[]>(endpoint);

  useEffect(() => {
    if (!endpoint) return;
    const t = setInterval(() => bookings.refetch(), 30_000);
    return () => clearInterval(t);
  }, [endpoint, bookings]);

  if (!currentOrg) return null;

  const venueLookup = new Map(venues.map((v) => [v.id, v.name]));
  const all = bookings.data ?? [];
  const filtered = all
    .filter((b) => (venueId ? b.venueId === venueId : true))
    .filter((b) => (tab === "all" ? true : b.status === tab))
    .sort((a, b) => +new Date(b.scheduledFor) - +new Date(a.scheduledFor));

  async function confirmBooking(b: BookingRow) {
    setBusy(b.id);
    try {
      const res = await api(`/api/bookings/${b.id}/confirm`, { method: "POST" });
      const json = await res.json();
      if (!res.ok || !json.success) {
        addToast(json.error ?? "Failed to confirm booking", "error");
        return;
      }
      addToast(json.data?.hubOffline ? "Confirmed (hub offline — will sync)" : "Booking confirmed");
      bookings.refetch();
    } finally {
      setBusy(null);
    }
  }

  async function cancelBooking(b: BookingRow) {
    setBusy(b.id);
    try {
      const res = await api(`/api/bookings/${b.id}/cancel`, { method: "POST" });
      const json = await res.json();
      if (!res.ok || !json.success) {
        addToast(json.error ?? "Failed to cancel booking", "error");
        return;
      }
      addToast("Booking cancelled");
      bookings.refetch();
    } finally {
      setBusy(null);
      setConfirmCancel(null);
    }
  }

  const columns: Column<BookingRow>[] = [
    {
      key: "scheduledFor",
      header: "When",
      render: (b) => (
        <div>
          <div className="font-medium text-gray-900">{new Date(b.scheduledFor).toLocaleString()}</div>
          <div className="text-xs text-gray-500">{b.durationMin} min</div>
        </div>
      ),
    },
    {
      key: "customer",
      header: "Customer",
      render: (b) => (
        <div>
          <div className="text-sm text-gray-900">{b.customerName}</div>
          <div className="text-xs text-gray-500">{b.customerEmail || "—"}</div>
        </div>
      ),
    },
    {
      key: "venue",
      header: "Venue",
      render: (b) => <span className="text-sm text-gray-700">{venueLookup.get(b.venueId) ?? "—"}</span>,
    },
    {
      key: "status",
      header: "Status",
      render: (b) => <Badge className={STATUS_COLOURS[b.status]}>{b.status}</Badge>,
    },
    {
      key: "source",
      header: "Source",
      render: (b) => (
        <span className="text-xs text-gray-500">
          {b.partnerSource ? `via ${b.partnerSource}` : "manual"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (b) => (
        <div className="text-right space-x-2">
          {b.status === "pending" && canIDo("bookings:confirm", currentOrg.id) && (
            <Button
              size="sm"
              loading={busy === b.id}
              onClick={() => confirmBooking(b)}
            >
              Confirm
            </Button>
          )}
          {b.status !== "active" &&
            b.status !== "completed" &&
            b.status !== "cancelled" &&
            canIDo("bookings:confirm", currentOrg.id) && (
              <Button
                size="sm"
                variant="secondary"
                loading={busy === b.id}
                onClick={() => setConfirmCancel(b)}
              >
                Cancel
              </Button>
            )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Bookings"
        subtitle={`Inbox for ${currentOrg.name}. Polls every 30 s.`}
      />

      <TabSwitcher<BookingStatus | "all">
        tabs={TABS.map((t) => ({
          value: t.key,
          label:
            t.key === "all"
              ? `All (${all.length})`
              : `${t.label} (${all.filter((b) => b.status === t.key).length})`,
        }))}
        value={tab}
        onChange={setTab}
      />

      <Card className="overflow-hidden">
        <DataState
          loading={bookings.loading && !bookings.data}
          error={bookings.error}
          empty={!bookings.loading && filtered.length === 0}
          emptyMessage={
            tab === "pending"
              ? "No pending bookings — you're all caught up."
              : "No bookings in this view."
          }
        >
          <Table<BookingRow> columns={columns} data={filtered} keyExtractor={(b) => b.id} />
        </DataState>
      </Card>

      <ConfirmDialog
        isOpen={!!confirmCancel}
        title="Cancel booking?"
        message={
          confirmCancel
            ? `Cancel ${confirmCancel.customerName}'s booking on ${new Date(confirmCancel.scheduledFor).toLocaleString()}? The customer will not be notified automatically.`
            : ""
        }
        confirmLabel="Cancel booking"
        loading={busy === confirmCancel?.id}
        onClose={() => setConfirmCancel(null)}
        onConfirm={() => confirmCancel && cancelBooking(confirmCancel)}
      />
    </div>
  );
}
